import { useState } from "react";
import { toast } from "sonner";
import { PageHero, SectionHeading } from "@/components/site/Sections";
import { Wizard } from "@/components/site/Wizard";
import {
  StepGrid, StepTitle, Text, Money, Num, Select, Area, Chips, MultiChips, SystemsGrid, UploadStub,
} from "@/components/site/WizardControls";
import { submitSeller } from "@/lib/api";
import { IMAGES } from "@/data/content";
import { CheckCircle2 } from "lucide-react";

const PROP_TYPES = ["Single Family", "Duplex", "Triplex", "Fourplex", "Multifamily", "Condo", "Townhouse", "Mobile Home", "Mixed Use", "Commercial", "Land", "Other"];
const OCC = ["Owner Occupied", "Tenant Occupied", "Vacant", "Partially Occupied", "Unknown"];
const REHAB = ["Move-In Ready / Minimal Repairs", "Cosmetic Rehab", "Medium Rehab", "Heavy Rehab", "Full Gut Renovation", "Unknown"];
const SYSTEMS = ["Roof", "Foundation", "Electrical", "Plumbing", "HVAC", "Water Heater", "Windows", "Kitchen", "Bathrooms", "Flooring", "Walls / Ceilings", "Basement", "Exterior / Siding", "Garage", "Driveway", "Sewer / Septic", "Well"];
const ISSUES = ["Water Damage", "Mold", "Fire Damage", "Foundation Problems", "Roof Leaks", "Plumbing Problems", "Electrical Problems", "Structural Damage", "Termites / Pest Damage", "Code Violations", "Open Permits", "Flooding", "Sewer Problems", "Septic Problems", "Hoarding / Excessive Contents", "Major Debris", "Tenant Problems", "None Known", "Other"];
const OWN = ["Yes", "No", "Under Contract", "Inherited / Estate", "Other"];
const YNU = ["Yes", "No", "Unknown"];
const YNN = ["Yes", "No", "Not Sure"];
const LEGAL = ["Probate", "Estate", "Divorce", "Foreclosure", "Tax Foreclosure", "Bankruptcy", "Code Enforcement", "None", "Other"];
const TIMELINE = ["Immediately", "Within 7 Days", "Within 14 Days", "Within 30 Days", "Within 60 Days", "60+ Days", "Just Exploring Options"];
const REASON = ["Property Needs Repairs", "Inherited Property", "Vacant Property", "Tenant Issues", "Tired Landlord", "Relocation", "Financial Reasons", "Foreclosure", "Tax Issues", "Downsizing", "Portfolio Sale", "Selling Investment Property", "Other", "Prefer Not to Say"];
const ACCESS = ["Yes", "No", "Maybe", "Need to Coordinate"];
const ACCESS_METHOD = ["Owner Present", "Lockbox", "Tenant Coordination", "Agent Coordination", "Other"];
const CONTACT_METHOD = ["Phone", "Text", "Email"];
const CONTACT_TIME = ["Morning", "Afternoon", "Evening", "Anytime"];

export default function Sell() {
  const [f, setF] = useState({ occupancy: "Unknown", rehab_level: "Unknown", systems: {}, issues: [], own_property: "Yes", multiple_owners: "No", all_willing: "Yes", has_mortgage: "Unknown", taxes_current: "Unknown", liens: "Unknown", legal_status: "None", property_access: "Yes", contact_method: "Phone", contact_time: "Anytime" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const setSys = (item, val) => setF((p) => ({ ...p, systems: { ...p.systems, [item]: val } }));

  const onSubmit = async () => {
    if (!f.first_name || !f.last_name || !f.phone || !f.email || !f.property_address) {
      toast.error("Please complete property address and contact details.");
      return;
    }
    setSending(true);
    try {
      await submitSeller(f);
      setDone(true);
      toast.success("Property received for review.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const steps = [
    { key: "Property", title: "Property Information", render: () => (
      <StepGrid>
        <StepTitle>Property</StepTitle>
        <Text label="Property Address" value={f.property_address} onChange={set("property_address")} required full testid="sell-address" />
        <Text label="City" value={f.city} onChange={set("city")} />
        <Text label="County" value={f.county} onChange={set("county")} />
        <Text label="State" value={f.state} onChange={set("state")} />
        <Text label="ZIP Code" value={f.zip_code} onChange={set("zip_code")} />
        <Text label="Parcel Number (optional)" value={f.parcel_number} onChange={set("parcel_number")} />
        <Select label="Property Type" value={f.property_type} onChange={set("property_type")} options={PROP_TYPES} testid="sell-prop-type" />
        <Num label="Bedrooms" value={f.bedrooms} onChange={set("bedrooms")} />
        <Num label="Bathrooms" value={f.bathrooms} onChange={set("bathrooms")} />
        <Num label="Approx. Square Feet" value={f.square_feet} onChange={set("square_feet")} />
        <Text label="Lot Size (optional)" value={f.lot_size} onChange={set("lot_size")} />
        <Num label="Year Built (optional)" value={f.year_built} onChange={set("year_built")} />
        <Chips label="Occupancy" options={OCC} value={f.occupancy} onChange={set("occupancy")} testid="sell-occupancy" />
      </StepGrid>
    )},
    { key: "Price", title: "Expected Sale Price", render: () => (
      <StepGrid>
        <StepTitle hint="It's okay to estimate — leave blank if you don't know.">How much are you hoping to sell for?</StepTitle>
        <Money label="Expected Sale Amount" value={f.expected_sale_amount} onChange={set("expected_sale_amount")} testid="sell-expected" />
        <Money label="Minimum You Would Consider" value={f.minimum_amount} onChange={set("minimum_amount")} optional />
        <Money label="Estimated Current Value" value={f.current_value} onChange={set("current_value")} optional />
        <Money label="Estimated ARV" value={f.arv} onChange={set("arv")} optional />
        <Money label="Estimated Rehab Cost" value={f.rehab_cost} onChange={set("rehab_cost")} optional />
        <Money label="Mortgage Balance" value={f.mortgage_balance} onChange={set("mortgage_balance")} optional />
        <Money label="Other Loan Balance" value={f.other_loan} onChange={set("other_loan")} optional />
        <Money label="Known Liens" value={f.known_liens} onChange={set("known_liens")} optional />
        <Money label="Back Taxes" value={f.back_taxes} onChange={set("back_taxes")} optional />
        <Money label="HOA Balance" value={f.hoa_balance} onChange={set("hoa_balance")} optional />
      </StepGrid>
    )},
    { key: "Condition", title: "Condition & Issues", render: () => (
      <StepGrid>
        <StepTitle>What condition is the property in?</StepTitle>
        <Chips label="Renovation level needed" options={REHAB} value={f.rehab_level} onChange={set("rehab_level")} testid="sell-rehab-level" />
        <SystemsGrid label="Condition of systems" items={SYSTEMS} value={f.systems} onChange={setSys} testid="sell-systems" />
        <MultiChips label="Known issues (select all that apply)" options={ISSUES} values={f.issues} onChange={set("issues")} testid="sell-issues" />
        <Area label="Notes about condition" value={f.condition_notes} onChange={set("condition_notes")} />
        {f.occupancy === "Tenant Occupied" && (
          <>
            <StepTitle hint="Since the property is tenant occupied.">Tenant Information</StepTitle>
            <Money label="Monthly Rent" value={f.monthly_rent} onChange={set("monthly_rent")} />
            <Text label="Lease Expiration" type="date" value={f.lease_exp} onChange={set("lease_exp")} />
            <Chips label="Tenant current on rent?" options={YNU} value={f.tenant_current} onChange={set("tenant_current")} />
            <Chips label="Lease available?" options={["Yes", "No"]} value={f.lease_available} onChange={set("lease_available")} />
          </>
        )}
      </StepGrid>
    )},
    { key: "Ownership", title: "Ownership", render: () => (
      <StepGrid>
        <StepTitle>Ownership</StepTitle>
        <Chips label="Do you currently own the property?" options={OWN} value={f.own_property} onChange={set("own_property")} />
        <Chips label="Are there multiple owners?" options={YNU} value={f.multiple_owners} onChange={set("multiple_owners")} />
        <Chips label="Are all owners willing to sell?" options={YNN} value={f.all_willing} onChange={set("all_willing")} />
        <Chips label="Is there a mortgage?" options={YNU} value={f.has_mortgage} onChange={set("has_mortgage")} />
        <Chips label="Are property taxes current?" options={YNU} value={f.taxes_current} onChange={set("taxes_current")} />
        <Chips label="Any known liens or judgments?" options={YNU} value={f.liens} onChange={set("liens")} />
        <Chips label="Is the property involved in any of these?" options={LEGAL} value={f.legal_status} onChange={set("legal_status")} />
      </StepGrid>
    )},
    { key: "Timeline", title: "Timeline", render: () => (
      <StepGrid>
        <StepTitle>When would you like to sell?</StepTitle>
        <Chips label="Timeline" options={TIMELINE} value={f.timeline} onChange={set("timeline")} testid="sell-timeline" />
        <Chips label="Reason for selling (optional)" options={REASON} value={f.reason} onChange={set("reason")} />
      </StepGrid>
    )},
    { key: "Media", title: "Photos & Links", render: () => (
      <StepGrid>
        <StepTitle hint="Photos help us evaluate faster. Optional but recommended.">Photos & Property Link</StepTitle>
        <Text label="Property Link (Zillow, Redfin, Realtor.com, MLS, Auction — optional)" value={f.property_link} onChange={set("property_link")} full testid="sell-link" />
        <UploadStub label="Upload Property Photos (optional)" testid="sell-files" onNote={() => toast.info("Photos noted. A coordinator will request secure uploads.")} />
        <Chips label="Can the property be accessed for an inspection?" options={ACCESS} value={f.property_access} onChange={set("property_access")} />
        <Select label="Access Method" value={f.access_method} onChange={set("access_method")} options={ACCESS_METHOD} />
        <Area label="Access Notes (optional)" value={f.access_notes} onChange={set("access_notes")} />
      </StepGrid>
    )},
    { key: "Contact", title: "Contact", render: () => (
      <StepGrid>
        <StepTitle>How can we reach you?</StepTitle>
        <Text label="First Name" value={f.first_name} onChange={set("first_name")} required testid="sell-first-name" />
        <Text label="Last Name" value={f.last_name} onChange={set("last_name")} required testid="sell-last-name" />
        <Text label="Company (optional)" value={f.company} onChange={set("company")} />
        <Text label="Phone" type="tel" value={f.phone} onChange={set("phone")} required testid="sell-phone" />
        <Text label="Email" type="email" value={f.email} onChange={set("email")} required testid="sell-email" />
        <Chips label="Preferred Contact Method" options={CONTACT_METHOD} value={f.contact_method} onChange={set("contact_method")} />
        <Chips label="Best Contact Time" options={CONTACT_TIME} value={f.contact_time} onChange={set("contact_time")} />
      </StepGrid>
    )},
  ];

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Submitted" title="Property Received." />
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-xl px-4 text-center lg:px-8">
            <CheckCircle2 className="mx-auto h-16 w-16 text-gold" />
            <p className="mt-6 text-sm leading-relaxed text-slate-600">
              Thank you. Vertexhub Ventures has received your property information for review. Submission does not
              guarantee an offer or purchase.
            </p>
            <a href="/" className="mt-8 inline-block rounded-full border border-gold/60 px-6 py-3 text-sm font-semibold text-gold hover:bg-gold/10">Back to Home</a>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        eyebrow="I Want to Sell"
        title="I Want to Sell My Property."
        subtitle="Tell us about your property, the condition, your expected sale amount, and your timeline. Vertexhub Ventures will review the opportunity and determine possible next steps."
        image={IMAGES.spacious}
      />
      <section className="border-t border-slate-200 bg-[#F5F3EC] py-16 lg:py-24">
        <div className="mx-auto max-w-3xl px-4 lg:px-8">
          <SectionHeading center eyebrow="Property Evaluation" title="Submit My Property" light={false} />
          <div className="mt-10">
            <Wizard steps={steps} onSubmit={onSubmit} submitting={sending} submitLabel="Submit My Property" testid="seller-form" />
          </div>
        </div>
      </section>
    </div>
  );
}
